import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion, AnimatePresence } from "framer-motion";
import { Message } from "../types/chat";
import "../styles/typography.css";
import styles from "../styles/ChatClassification.module.css";

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
}

const messageVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

const ChatMessages: React.FC<ChatMessagesProps> = ({ messages, isLoading }) => (
  <div className={styles.chatMessages}>
    <AnimatePresence initial={false}>
      {messages.map((msg) => (
        <motion.div
          key={msg.id}
          className={`${styles.messageRow} ${msg.sender === "bot" ? styles.bot : styles.user}`}
          variants={messageVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          layout
        >
          {msg.sender === "bot" && (
            <img
              src="/Chatwidget/images/only_logo_insia.png"
              alt="Bot"
              className={styles.messageAvatar}
            />
          )}

          <div className={styles.messageBlock}>
            {/* Balão único */}
            <motion.div className={styles.messageBubble} layout>
              {msg.sender === "bot" ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  className={styles.markdownContent}
                  components={{
                    p: ({ node, ...props }) => <div {...props} />,
                    a: ({ node, href, children, ...props }) => {
                      const youtubeMatch = href?.match(
                        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/
                      );
                      if (youtubeMatch) {
                        const videoId = youtubeMatch[1];
                        const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                        return (
                          <div className={styles.youtubeContainer}>
                            <iframe
                              src={embedUrl}
                              title="YouTube video"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        );
                      }
                      return (
                        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                          {children}
                        </a>
                      );
                    },
                    br: () => null,
                  }}
                >
                  {msg.text}
                </ReactMarkdown>
              ) : (
                msg.text
              )}

              {/* Fontes, classificação, cache, debug — tudo dentro do balão */}
              {msg.sources && msg.sources.length > 0 && (
                <div className={styles.messageSources}>
                  <h4>📚 Fontes:</h4>
                  {msg.sources.map((source, index) => (
                    <div key={index} className={styles.sourceItem}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => <div {...props} />,
                          a: ({ node, href, children, ...props }) => (
                            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {source}
                      </ReactMarkdown>
                    </div>
                  ))}
                </div>
              )}

              {msg.classification && (
                <div className={styles.messageMeta}>📊 Classificação: {msg.classification}</div>
              )}
              {msg.cached && <div className={styles.messageMeta}>⚡ Resposta do cache</div>}
              {msg.debug && (
                <div className={styles.messageMeta}>
                  🐛 Debug: {msg.debug.processing_time || "N/A"}
                </div>
              )}
            </motion.div>

            {/* Hora em baixo à direita */}
            {msg.timestamp && (
              <motion.div
                className={styles.messageTime}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {msg.timestamp}
              </motion.div>
            )}
          </div>
        </motion.div>
      ))}

      {/* Indicador de escrita */}
      {isLoading && (
        <div className={`${styles.messageRow} ${styles.bot}`}>
          <img
            src="/ChatWidget/images/only_logo_insia.png"
            alt="Bot"
            className={styles.messageAvatar}
          />
          <div className={styles.messageBlock}>
            <div className={styles.messageBubble}>
              <div className={styles.typingIndicator}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>
  </div>
);

export default ChatMessages;
